import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import InboxRoundedIcon from '@mui/icons-material/InboxRounded'
import API from '../../../services/API'
import { NotificationRow, getWebPath } from './notificationRendering'

// Same inbox the mobile app reads (GET /account/notifications) - every
// notification is stored there whichever channel delivered it. The popover
// shows the latest 30; the full history lives on /notifications. Row
// rendering is shared with that page (notificationRendering.jsx).

const POLL_MS = 60 * 1000

const NotificationBell = ({ TaskBadge }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [anchorEl, setAnchorEl] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(() => {
    return API.get('/account/notifications?limit=30')
      .then((res) => {
        if (res.status === 200) {
          setNotifications(res.data?.notifications ?? [])
          setUnreadCount(res.data?.unreadCount ?? 0)
          setLoaded(true)
        }
      })
      .catch(() => {})
  }, [])

  // Refresh on navigation, on a timer while the tab is visible, and when the
  // tab regains focus - cheap (one indexed query) and keeps the badge honest.
  useEffect(() => {
    load()
  }, [load, location.pathname])

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') load()
    }
    const timer = setInterval(tick, POLL_MS)
    window.addEventListener('focus', tick)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', tick)
    }
  }, [load])

  const open = (event) => {
    setAnchorEl(event.currentTarget)
    if (!loaded) setLoading(true)
    load().finally(() => setLoading(false))
  }

  const markRead = (ids) => {
    const now = new Date().toISOString()
    setNotifications((prev) =>
      prev.map((n) =>
        !ids || ids.includes(n._id) ? { ...n, readAt: n.readAt ?? now } : n,
      ),
    )
    setUnreadCount((prev) => (ids ? Math.max(prev - ids.length, 0) : 0))
    API.post('/account/notifications/mark-read', ids ? { ids } : {}).catch(
      () => {},
    )
  }

  const handleClick = (notification) => {
    if (!notification.readAt) markRead([notification._id])
    const path = getWebPath(notification)
    if (path) {
      setAnchorEl(null)
      navigate(path)
    }
  }

  const BellIcon = unreadCount
    ? NotificationsActiveRoundedIcon
    : NotificationsNoneRoundedIcon

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          size="small"
          onClick={open}
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
          sx={{ p: 0 }}
        >
          <TaskBadge
            badgeContent={unreadCount}
            max={99}
            color="contrast"
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { top: 4 } }}
          >
            <BellIcon sx={{ color: 'primary.main' }} />
          </TaskBadge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: 'calc(100vw - 24px)', sm: 400 },
              maxHeight: 520,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow: '0 18px 50px rgba(15,23,42,0.18)',
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25 }}
        >
          <Stack direction="row" alignItems="baseline" gap={1}>
            <Typography fontWeight={800}>Notifications</Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {unreadCount} unread
              </Typography>
            )}
          </Stack>
          <Button
            size="small"
            disabled={!unreadCount}
            onClick={() => markRead(null)}
          >
            Mark all read
          </Button>
        </Stack>
        <Divider />

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={22} />
            </Stack>
          )}
          {!loading && notifications.length === 0 && (
            <Stack alignItems="center" gap={0.75} sx={{ py: 5, px: 2 }}>
              <InboxRoundedIcon color="disabled" />
              <Typography variant="body2" fontWeight={700}>
                You&apos;re all caught up
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                Shift updates, approvals and alerts for you will appear here.
              </Typography>
            </Stack>
          )}
          {!loading && notifications.length > 0 && (
            <Box component="ul" sx={{ m: 0, p: 0 }}>
              {notifications.map((notification) => (
                <NotificationRow
                  key={notification._id}
                  notification={notification}
                  onClick={handleClick}
                />
              ))}
            </Box>
          )}
        </Box>

        <Divider />
        <Button
          fullWidth
          size="small"
          onClick={() => {
            setAnchorEl(null)
            navigate('/notifications')
          }}
          sx={{ py: 1, borderRadius: 0 }}
        >
          View all notifications
        </Button>
      </Popover>
    </>
  )
}

export default NotificationBell
